/**
 * Single source of truth for lead-capture validation.
 *
 * Used by BOTH `/api/validate-email` (the debounced, interactive check) and the
 * `saveDownloadLead` server action (the authoritative check). The server action
 * never trusts the client's `verified` state — it re-runs these rules itself.
 */

export const MAX_NAME_LENGTH = 100;
export const MAX_EMAIL_LENGTH = 254; // RFC 5321

/**
 * Free / personal / disposable providers. The existing business rule is that
 * gated downloads require a work or organisational address, so these are rejected.
 * This list is intentionally unchanged in substance from the original
 * implementation in `src/app/api/validate-email/route.ts`.
 */
export const FREE_EMAIL_PROVIDERS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mail.com',
  'aol.com',
  'gmx.com',
  'zoho.com',
  'protonmail.com',
  'proton.me',
  'tutanota.com',
  'yandex.com',
  'mailinator.com',
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'throwaway.email',
  'temp-mail.org',
  'maildrop.cc',
  'sharklasers.com',
  'trashmail.com',
  'fakeinbox.com',
  'yopmail.com',
  'temp-mail.io',
  'mockemail.com',
  'dispostable.com',
  'grr.la',
  'trashmail.ws',
  'tempmail.us',
  'emailondeck.com',
  'e4ward.com',
  'nada.email',
  'dummy.com',
  'test.com',
  'example.com',
  'example.org',
  'test.net',
  'invalid.com',
]);

/**
 * Deliberately stricter than the original `/^[^\s@]+@[^\s@]+\.[^\s@]+$/`:
 * that pattern accepted values such as `a@b.c d` is rejected only by the \s class,
 * but still allowed consecutive dots and a leading/trailing dot in the domain.
 */
const EMAIL_PATTERN =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

export type LeadFieldError = {
  field: 'fullName' | 'workEmail';
  code:
    | 'NAME_REQUIRED'
    | 'NAME_TOO_LONG'
    | 'EMAIL_REQUIRED'
    | 'EMAIL_TOO_LONG'
    | 'EMAIL_MALFORMED'
    | 'EMAIL_NOT_ORGANISATIONAL';
  message: string;
};

export type NormalisedLead = {
  fullName: string;
  workEmail: string;
  domain: string;
};

export type LeadValidationResult =
  | { ok: true; value: NormalisedLead }
  | { ok: false; error: LeadFieldError };

/** Collapses internal whitespace and trims. Does not alter letter case. */
export function normaliseName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw.replace(/\s+/g, ' ').trim();
}

/**
 * Trims and lowercases the domain portion only. The local part of an address is
 * case-sensitive per RFC 5321, so it is preserved as typed.
 */
export function normaliseEmail(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  const at = trimmed.lastIndexOf('@');
  if (at === -1) return trimmed;
  return `${trimmed.slice(0, at)}@${trimmed.slice(at + 1).toLowerCase()}`;
}

export function isEmailSyntaxValid(email: string): boolean {
  return email.length <= MAX_EMAIL_LENGTH && EMAIL_PATTERN.test(email);
}

export function getEmailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).toLowerCase();
}

export function isOrganisationalDomain(domain: string): boolean {
  return domain.length > 0 && !FREE_EMAIL_PROVIDERS.has(domain);
}

/**
 * Authoritative validation for a lead submission. Pure and synchronous, so it is
 * cheap to unit test and safe to run on both client and server.
 */
export function validateLead(input: {
  fullName?: unknown;
  workEmail?: unknown;
}): LeadValidationResult {
  const fullName = normaliseName(input.fullName);
  const workEmail = normaliseEmail(input.workEmail);

  if (!fullName) {
    return {
      ok: false,
      error: { field: 'fullName', code: 'NAME_REQUIRED', message: 'Please enter your full name.' },
    };
  }

  if (fullName.length > MAX_NAME_LENGTH) {
    return {
      ok: false,
      error: {
        field: 'fullName',
        code: 'NAME_TOO_LONG',
        message: `Please keep your name under ${MAX_NAME_LENGTH} characters.`,
      },
    };
  }

  if (!workEmail) {
    return {
      ok: false,
      error: {
        field: 'workEmail',
        code: 'EMAIL_REQUIRED',
        message: 'Please enter your work email address.',
      },
    };
  }

  if (workEmail.length > MAX_EMAIL_LENGTH) {
    return {
      ok: false,
      error: {
        field: 'workEmail',
        code: 'EMAIL_TOO_LONG',
        message: 'That email address is too long.',
      },
    };
  }

  if (!isEmailSyntaxValid(workEmail)) {
    return {
      ok: false,
      error: {
        field: 'workEmail',
        code: 'EMAIL_MALFORMED',
        message: 'Please enter a valid email address.',
      },
    };
  }

  const domain = getEmailDomain(workEmail);

  if (!isOrganisationalDomain(domain)) {
    return {
      ok: false,
      error: {
        field: 'workEmail',
        code: 'EMAIL_NOT_ORGANISATIONAL',
        message: 'Please use your work or organisational email address.',
      },
    };
  }

  return { ok: true, value: { fullName, workEmail, domain } };
}
